import { useState, Fragment, useMemo, useEffect } from "react";
import styled from "styled-components";
import { Media } from "../types/media";
import {
  CarouselImage,
  PaddedContainer,
  ThumbnailImage,
} from "../styles/styles";
import { useImagesByMediaId } from "../hooks/useImagesByMediaId";
import FixaCarousel, {
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { DesktopImageViewer } from "./DesktopImageViewer";
import { MobileImageViewer } from "./MobileImageViewer";
import { DesktopOnly } from "@coworker/apprestructured/src/shared/components/DesktopOnly";
import { MobileOnly } from "@coworker/apprestructured/src/shared/components/MobileOnly";
import { useTranslation } from "@coworker/locales";
import { AddImagesModal } from "@coworker/apprestructured/src/task-management/components/AddImagesModal/AddImagesModal";
import { imageToExistingFile } from "../Helpers/imageHelpers";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useWriteSignedUrls } from "../hooks/useWriteSignedUrls";
import {
  useImageUploader,
  useWaitForImageUpload,
} from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useSyncMediaImages } from "../hooks/useSyncMediaImages";
import { AddImagesToNoImagesMedia } from "./AddImagesToNoImagesMedia";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { MediaImagesCarouselSkeleton } from "./skeletons/MediaImagesCarouselSkeleton";

const ImageCarouselContainer = styled.div`
  position: relative;
`;

const OverlayButton = styled.div`
  position: absolute;
  bottom: 48px;
  right: 12px;
  z-index: 1;
`;
interface MediaImagesCarouselProps {
  media: Media;
  groupId?: string;
}

export const MediaImagesCarousel = ({
  media,
  groupId,
}: MediaImagesCarouselProps) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [addImagesModalOpen, setAddImagesModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slidingIndex, setSlidingIndex] = useState(0);
  const [storedImages, setStoredImages] = useState<NewOrExistingFile[]>([]);
  const [currentImages, setCurrentImages] = useState<NewOrExistingFile[]>([]);
  const { data: images, isLoading: imagesLoading } = useImagesByMediaId(
    media.id,
    groupId
  );

  useEffect(() => {
    if (!images) return;
    const existingFiles = images.map((image) => imageToExistingFile(image));
    setStoredImages(existingFiles);
    setCurrentImages(existingFiles);
  }, [images]);

  const useWriteSignedUrlsFunc = useMemo(() => useWriteSignedUrls, []);
  const imageSize = useMemo(() => ({ maxWidthOrHeight: 1024 }), []);

  const imageUploader = useImageUploader(
    currentImages,
    imageSize,
    useWriteSignedUrlsFunc
  );

  const { upload: uploadImages } = useWaitForImageUpload(() => imageUploader);

  const { createUploads: imagesCreateUploads, error: uploadImagesError } =
    imageUploader;

  const { syncMediaImages, error: syncMediaImagesError } = useSyncMediaImages({
    storedImages,
    currentImages,
    imagesCreateUploads,
  });

  const handleAddImagesModalConfirmClick = async () => {
    setSaving(true);

    try {
      await uploadImages();
      await syncMediaImages(media.id, groupId);
      setAddImagesModalOpen(false);
    } catch (error) {
      reportMessageToSentry(
        "RoomSettings: failed to upload/sync media images",
        {
          mediaId: media.id,
          groupId,
          uploadImagesError,
          syncMediaImagesError,
          errorMessage: error instanceof Error ? error.message : undefined,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshClick = () => {
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  const showSomethingWentWrongPrompt: boolean =
    !!uploadImagesError || !!syncMediaImagesError;

  if (imagesLoading) {
    return <MediaImagesCarouselSkeleton />;
  }

  if (!images || images.length === 0) {
    return <AddImagesToNoImagesMedia mediaId={media.id} groupId={groupId} />;
  }

  return (
    <>
      <PaddedContainer $py={"0px"} $px={"16px"}>
        <ImageCarouselContainer>
          <FixaCarousel
            id="imagesByMedia"
            data-testid="imagesByMedia"
            slideIndex={slidingIndex}
            hasSlideCounter={true}
            onVisibleSlideChange={(visibleIndices) => {
              if (visibleIndices.length > 0) {
                const indexed = visibleIndices[0] ?? 0;
                setSlidingIndex(indexed);
              }
            }}
          >
            {images.map((image, index) => {
              const imageProps = {
                src: image?.url ?? "",
                $rotation: image?.rotation || 0,
                $clickable: true as const,
                fallback: !image?.url || image.url.trim() === "",
                onClick: () => setOpenModal(true),
              };

              return (
                <Fragment key={image?.id ?? index}>
                  <DesktopOnly>
                    <CarouselImage {...imageProps} $variant="desktop" />
                  </DesktopOnly>
                  <MobileOnly>
                    <CarouselImage {...imageProps} $variant="mobile" />
                  </MobileOnly>
                </Fragment>
              );
            })}
          </FixaCarousel>
          <OverlayButton>
            <FixaButton
              loading={saving}
              type="emphasised"
              size="small"
              onClick={() => setAddImagesModalOpen(true)}
              text={t("addImagesString")}
            />
          </OverlayButton>
        </ImageCarouselContainer>
        <FixaOverflowCarousel
          id="thumbnailImagesByMedia"
          data-testid="thumbnailImagesByMedia"
          slideIndex={slidingIndex}
        >
          {images.map((image, index) => {
            const imageProps = {
              src: image?.url ?? "",
              $rotation: image?.rotation || 0,
              $selected: slidingIndex === index,
              onClick: () => setSlidingIndex(index),
            };

            return (
              <Fragment key={image?.id ?? index}>
                <ThumbnailImage {...imageProps} />
              </Fragment>
            );
          })}
        </FixaOverflowCarousel>
      </PaddedContainer>

      <DesktopOnly>
        {openModal && (
          <DesktopImageViewer
            images={images}
            isOpen={openModal}
            onClose={() => setOpenModal(false)}
            slideIndex={slidingIndex}
          />
        )}
      </DesktopOnly>

      <MobileOnly>
        {openModal && (
          <MobileImageViewer
            images={images}
            isOpen={openModal}
            onClose={() => setOpenModal(false)}
            slideIndex={slidingIndex}
          />
        )}
      </MobileOnly>
      <AddImagesModal
        isOpen={addImagesModalOpen}
        images={currentImages}
        onImagesChange={setCurrentImages}
        onClose={() => setAddImagesModalOpen(false)}
        onConfirmClick={handleAddImagesModalConfirmClick}
      />
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </>
  );
};
